/**
 * The graphics API client. Same origin (/api, proxied in development). In PRISM (M5) the editor token from
 * host:init is kept in memory only and sent as a bearer; it never goes in a URL, a cookie or a log.
 */
import type { Aspect, Asset, Scene } from '@prism-graphics/scene-schema';

let token: string | null = null;

export function setToken(value: string | null): void {
  token = value;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;
  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request(method: string, path: string, init: { json?: unknown; body?: BodyInit; headers?: Record<string, string>; keepalive?: boolean } = {}): Promise<Response> {
  const headers: Record<string, string> = { ...(init.headers ?? {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  let body = init.body;
  if (init.json !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(init.json);
  }
  // Browsers refuse keepalive bodies over 64 KiB (in total across pending keepalive requests): a larger body goes
  // as an ordinary request, which may still finish while the page unloads (best effort).
  const keepalive = init.keepalive === true && keepaliveFits(body);
  let res: Response;
  try {
    res = await fetch(path, { method, headers, ...(body !== undefined ? { body } : {}), credentials: 'omit', cache: 'no-store', ...(keepalive ? { keepalive: true } : {}) });
  } catch (e) {
    throw new ApiError(0, 'NETWORK', e instanceof Error ? e.message : 'Network error');
  }
  if (!res.ok) {
    let err: { code?: string; message?: string; details?: Record<string, unknown> } = {};
    try {
      err = ((await res.json()) as { error?: typeof err }).error ?? {};
    } catch {
      // not JSON
    }
    throw new ApiError(res.status, err.code ?? `HTTP_${res.status}`, err.message ?? res.statusText, err.details);
  }
  return res;
}

/** Browsers' keepalive body quota (64 KiB), less a margin for other pending keepalive requests. */
export const KEEPALIVE_MAX_BYTES = 60 * 1024;

function keepaliveFits(body: BodyInit | undefined): boolean {
  if (body === undefined) return true;
  if (typeof body === 'string') return body.length <= KEEPALIVE_MAX_BYTES / 3 || new TextEncoder().encode(body).byteLength <= KEEPALIVE_MAX_BYTES;
  if (body instanceof Blob) return body.size <= KEEPALIVE_MAX_BYTES;
  if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) return body.byteLength <= KEEPALIVE_MAX_BYTES;
  return false;
}

const json = async <T>(p: Promise<Response>): Promise<T> => (await p).json() as Promise<T>;

export interface FrameRate {
  num: number;
  den: number;
}

export interface DraftSummary {
  id: string;
  name: string;
  revision: number;
  sceneId: string;
  canvas: { width: number; height: number; frameRate: FrameRate };
  createdAt: string;
  updatedAt: string;
  prismAssetId?: string | null;
  prismBaseVersion?: number | null;
  prismSceneHash?: string | null;
}

export interface Draft extends DraftSummary {
  scene: Scene;
}

export interface LibraryAsset {
  hash: string;
  sha256: string;
  kind: 'font' | 'image' | 'video';
  mime: string;
  size: number;
  originalName: string;
  fileName: string;
  builtin: boolean;
  createdAt: string;
  url: string;
  meta: {
    family?: string;
    subfamily?: string;
    fontWeight?: string;
    fontStyle?: 'normal' | 'italic';
    variable?: boolean;
    codePoints?: Array<[number, number]>;
    coverage?: Record<string, { complete: boolean; missing: string[] }>;
    width?: number;
    height?: number;
    sanitized?: boolean;
    license?: string;
    /** Video: the track carries alpha (WebM AlphaMode 1); MP4 never does. */
    alpha?: boolean;
    codec?: string;
  };
  sceneAsset: Asset;
}

export interface ClientConfig {
  version: string;
  authMode: 'dev' | 'prism' | 'mock';
  glyphLanguages: string[];
  limits: { fontBytes: number; imageBytes: number; videoBytes: number; packageBytes: number };
  prismOrigins: string[];
}

export interface Example {
  id: string;
  name: string;
  description?: string;
  canvas: { width: number; height: number; frameRate: FrameRate };
}

export interface CheckIssue {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

/** POST /api/v1/drafts/:id/check (apps/api/src/check/check.ts). */
export interface CheckReport {
  valid: boolean;
  errors: CheckIssue[];
  warnings: CheckIssue[];
  smoke: {
    ok: boolean;
    calls: Array<{ method: string; params?: unknown; statusCode?: number; statusMessage?: string; currentStep?: number; durationMs?: number; error?: string }>;
    consoleErrors: string[];
    failures: string[];
  } | null;
  thumbnail: { width: number; height: number; timestamp: number; dataUrl: string } | null;
  sizeBytes: number | null;
  sizeLimitBytes: number;
  manifestId: string | null;
  sceneHash: string | null;
  durationMs: number;
}

export interface ExportedPackage {
  blob: Blob;
  fileName: string;
  sizeBytes: number;
  sizeLimitBytes: number | undefined;
  thumbnail: 'rendered' | 'placeholder' | undefined;
  sceneHash: string | undefined;
}

export type Permission = 'view' | 'create' | 'edit' | 'save' | 'metadata';

export interface Me {
  userId: string;
  workspaceId: string;
  name: string;
  locale: string;
  permissions: Permission[];
  via: 'dev' | 'prism';
}

export type SaveMode = 'new' | 'version' | 'copy' | 'overwrite';

export interface SaveRequest {
  mode: SaveMode;
  name?: string;
  description?: string;
  tags?: string[];
  collectionId?: string;
  expectedVersion?: number;
  baseRevision?: number;
}

/** POST /api/v1/drafts/:id/save. */
export interface PrismSaved {
  assetId: string;
  version: number;
  name: string;
  created: boolean;
  sceneHash: string;
  sceneId: string;
  revision: number;
  updatedAt: string;
  warnings: CheckIssue[];
  /** The draft changed during the save (another tab, an autosave): the API kept the newer scene, unsaved in PRISM. */
  draftChanged?: boolean;
}

/** 409 VERSION_CONFLICT details. */
export interface VersionConflict {
  currentVersion: number;
  savedBy: { id: string; displayName: string } | null;
  savedAt: string | null;
}

export interface PrismOpened {
  assetId: string;
  version: number;
  currentVersion: number;
  name: string;
  readOnly: boolean;
  draftId?: string;
  sceneHash?: string;
  reason?: string;
  message?: string;
  manifest?: Record<string, unknown>;
  manifestPath?: string;
  packageUrl?: string;
  /** The caller's existing draft of this version was reused. */
  reused?: boolean;
  /** The caller has a draft of this version with unsaved changes: nothing was opened (ask: continue or fresh). */
  resumable?: { draftId: string; revision: number; updatedAt: string };
}

export interface TemplateSummary {
  id: string;
  name: string;
  description: string;
  category: string;
  builtin: boolean;
  /** The primary layout's canvas. */
  canvas: { width: number; height: number; frameRate: FrameRate };
  /** The shapes it has a layout for (16:9, 9:16, 4:5, 1:1 or other), the primary's first (ADR 0037). */
  layouts: Aspect[];
  stepCount: number;
  fields: Array<{ id: string; title: string; type: string }>;
}

export interface BrandTextStyle {
  name: string;
  font: string;
  fontWeight: number;
  fontStyle: 'normal' | 'italic';
  fontSize: number;
  lineHeight: number;
  letterSpacing: number;
  textTransform: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  color?: string;
}

export interface BrandKit {
  colors: Array<{ name: string; value: string }>;
  fonts: string[];
  textStyles: BrandTextStyle[];
  logos: string[];
}

export interface StoredBrandKit {
  kit: BrandKit;
  revision: number;
  updatedBy: string | null;
  updatedAt: string | null;
  canEdit: boolean;
}

export interface PrismCollection {
  id: string;
  parentId: string | null;
  name: string;
  canEdit: boolean;
}

export const api = {
  me: () => json<Me>(request('GET', '/api/v1/me')),
  saveToPrism: (draftId: string, body: SaveRequest) => json<PrismSaved>(request('POST', `/api/v1/drafts/${encodeURIComponent(draftId)}/save`, { json: body })),
  openFromPrism: (assetId: string, version?: number, resume?: 'continue' | 'fresh') =>
    json<PrismOpened>(request('POST', '/api/v1/prism/open', { json: { assetId, ...(version ? { version } : {}), ...(resume ? { resume } : {}) } })),
  templates: () => json<{ items: TemplateSummary[] }>(request('GET', '/api/v1/templates')),
  brandKit: () => json<StoredBrandKit>(request('GET', '/api/v1/brand-kit')),
  saveBrandKit: (baseRevision: number, kit: BrandKit) => json<StoredBrandKit>(request('PUT', '/api/v1/brand-kit', { json: { baseRevision, kit } })),
  prismCollections: () => json<{ items: PrismCollection[] }>(request('GET', '/api/v1/prism/collections')),
  /** A package from a same-origin API URL (e.g. PrismOpened.packageUrl). */
  packageBytes: async (path: string): Promise<Uint8Array> => {
    if (!path.startsWith('/api/v1/')) throw new ApiError(0, 'BAD_URL', 'Not an API path');
    return new Uint8Array(await (await request('GET', path)).arrayBuffer());
  },
  config: () => json<ClientConfig>(request('GET', '/api/v1/config')),
  listDrafts: () => json<{ items: DraftSummary[]; total: number }>(request('GET', '/api/v1/drafts?limit=200')),
  examples: () => json<{ items: Example[] }>(request('GET', '/api/v1/examples')),
  createDraft: (body: { name?: string; example?: string; template?: string; brand?: boolean; scene?: unknown; canvas?: { width: number; height: number; frameRate: FrameRate }; layouts?: Array<{ width: number; height: number }> }) =>
    json<Draft>(request('POST', '/api/v1/drafts', { json: body })),
  getDraft: (id: string) => json<Draft>(request('GET', `/api/v1/drafts/${encodeURIComponent(id)}`)),
  saveDraft: (id: string, baseRevision: number, scene: Scene, opts: { keepalive?: boolean } = {}) =>
    json<{ id: string; revision: number; updatedAt: string; name: string }>(request('PUT', `/api/v1/drafts/${encodeURIComponent(id)}`, { json: { baseRevision, scene }, ...opts })),
  deleteDraft: (id: string) => request('DELETE', `/api/v1/drafts/${encodeURIComponent(id)}`).then(() => undefined),
  exportDraft: async (id: string): Promise<ExportedPackage> => {
    const res = await request('POST', `/api/v1/drafts/${encodeURIComponent(id)}/export`);
    const cd = res.headers.get('Content-Disposition') ?? '';
    const fileName = /filename="([^"]+)"/.exec(cd)?.[1] ?? 'graphic.zip';
    const blob = await res.blob();
    const limit = Number(res.headers.get('X-Package-Size-Limit'));
    const thumb = res.headers.get('X-Thumbnail');
    return {
      blob,
      fileName,
      sizeBytes: blob.size,
      sizeLimitBytes: Number.isFinite(limit) && limit > 0 ? limit : undefined,
      thumbnail: thumb === 'rendered' || thumb === 'placeholder' ? thumb : undefined,
      sceneHash: res.headers.get('X-Scene-Hash') ?? undefined,
    };
  },
  checkDraft: (id: string) => json<CheckReport>(request('POST', `/api/v1/drafts/${encodeURIComponent(id)}/check`)),
  listAssets: () => json<{ items: LibraryAsset[] }>(request('GET', '/api/v1/assets')),
  uploadAsset: (file: File) =>
    json<LibraryAsset>(request('POST', '/api/v1/assets', { body: file, headers: { 'Content-Type': 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name) } })),
  assetBytes: async (sha256: string): Promise<ArrayBuffer> => (await request('GET', `/api/v1/assets/${sha256}`)).arrayBuffer(),
};
